import { useEffect, useRef, useState } from "react";

import type { AccountSwitcherPresentationProps } from "./AccountSwitcher.pres";
import type { AccountOption } from "./accountSwitcher.utils";

export type AccountProduct = AccountOption["product"];

export interface UseAccountSwitcherControllerParams {
  value: string | undefined;
  options: AccountOption[];
  triggerLabel?: string;
  isLoading?: boolean;
  onValueChange: (accountId: string) => void;
}

export interface AccountSwitcherControllerValue {
  presentation: AccountSwitcherPresentationProps;
}

export const useAccountSwitcherController = ({
  value,
  options,
  triggerLabel,
  isLoading = false,
  onValueChange,
}: UseAccountSwitcherControllerParams): AccountSwitcherControllerValue => {
  const [open, setOpen] = useState(false);
  const selectedOption = options.find((option) => option.id === value);
  const hasLiveAccounts = options.some((option) => option.product === "live");
  const hasPaperAccounts = options.some((option) => option.product === "paper");
  const fallbackProduct: AccountProduct = hasLiveAccounts ? "live" : "paper";
  const [activeProduct, setActiveProduct] = useState<AccountProduct>(
    selectedOption?.product ?? fallbackProduct,
  );
  const prevValueRef = useRef(value);

  useEffect(() => {
    if (!open) {
      prevValueRef.current = value;
      return;
    }

    if (value !== prevValueRef.current) {
      setActiveProduct(selectedOption?.product ?? fallbackProduct);
      prevValueRef.current = value;
    }
  }, [open, value, selectedOption?.product, fallbackProduct]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!options.some((option) => option.product === activeProduct)) {
      setActiveProduct(fallbackProduct);
    }
  }, [open, options, activeProduct, fallbackProduct]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setActiveProduct(selectedOption?.product ?? fallbackProduct);
    }
    setOpen(nextOpen);
  };

  const handleAccountSelect = (accountId: string) => {
    onValueChange(accountId);
    setOpen(false);
  };

  return {
    presentation: {
      value,
      options,
      triggerLabel,
      isLoading,
      open,
      onOpenChange: handleOpenChange,
      activeProduct,
      onActiveProductChange: setActiveProduct,
      hasLiveAccounts,
      hasPaperAccounts,
      onAccountSelect: handleAccountSelect,
    },
  };
};
